/**
 * The loyalty club's rules — the text a customer agrees to on /club/:code (spec §13).
 *
 * Legal text, so the OWNER decides it: the text is docs/superpowers/specs/2026-10-24-club-rules-draft.md, copied
 * here word for word (lib/club-rules.test.ts compares the two). v1 approved 2026-10-24 („დამტკიცებულია სენიორ");
 * v2 the same day („კი, v2 ჩაამატე") — item 5 names the controller and the processor in the law's words (Codex
 * final #13); no customer agreed to v1. Any change is a new CLUB_RULES_VERSION, approved by the owner again —
 * customers.club_rules_version records which text each customer agreed to. Georgian only, like every document the
 * shop gives a customer.
 */

export const CLUB_RULES_VERSION = 2

export type ClubRules = { title: string; items: { head: string; body: string }[] }

/** {shop}, {pct} and {threshold} are filled from the shop's own settings. */
export const CLUB_RULES_TEMPLATE: ClubRules = {
  title: '{shop} — მყიდველთა კლუბის წესები (ვერსია 2)',
  items: [
    { head: 'ბონუსი.', body: 'ყოველ ყიდვაზე გერიცხებათ ბონუსი: ნაყიდის {pct}%. ბონუსი ლარშია და მისი გამოყენება შეგიძლიათ შემდეგ ყიდვაზე, მხოლოდ ამ მაღაზიაში. ნაღდ ფულად არ გაიცემა.' },
    { head: 'კლუბის ფასი.', body: 'თუ ერთ თვეში დაგროვებული ბონუსი {threshold} ლარს მიაღწევს, მომდევნო ყიდვიდან კლუბის წევრი ხდებით და თქვენთვის მოქმედებს კლუბის ფასები. წევრობა გრძელდება მომდევნო თვის ბოლომდე. თუ ნაყიდს დააბრუნებთ და თვის ჯამი ზღვარს ქვემოთ ჩამოვა, წევრობა დაბრუნებისთანავე წყდება. უკვე ნაყიდზე კლუბის ფასი უკან არ მოითხოვება.' },
    { head: 'ბარათი.', body: 'ბარათი არის QR კოდი თქვენს ტელეფონში. ვინც მას სალაროსთან აჩვენებს, ის სარგებლობს ბონუსით და კლუბის ფასით, როგორც ნებისმიერი პლასტიკური ბარათით. დაკარგეთ ან სხვამ ნახა? ბარათის გვერდზე დააჭირეთ „ბარათის შეცვლა": ძველი ბარათი მაშინვე წყდება და ახალი იმეილზე მოგივათ.' },
    { head: 'რას ვინახავთ და რისთვის.', body: 'სახელს, ტელეფონის ნომერს, იმეილს და თქვენს ყიდვებს ამ მაღაზიაში. ბონუსის დასარიცხად, კლუბის ფასისთვის და ბარათის გამოსაგზავნად. იმეილზე მოდის მხოლოდ დასტურის კოდი და ბარათის ბმული. სარეკლამო წერილები არ იგზავნება.' },
    { head: 'ვინ პასუხობს მონაცემებზე.', body: 'მონაცემთა დამუშავებისთვის პასუხისმგებელი პირია {shop}. MartePOS მონაცემებს მაღაზიის დავალებით ამუშავებს, როგორც დამუშავებაზე უფლებამოსილი პირი.' },
    { head: 'როგორ დატოვოთ კლუბი.', body: 'მიმართეთ მაღაზიას. ის წაშლის თქვენს სახელს, ნომერს, იმეილს და ბარათს. დაუხარჯავი ბონუსი ამ დროს ნულდება. ყიდვების ჩანაწერი რჩება უსახელოდ, რადგან მაღაზიას აღრიცხვა ევალება. თუ მაღაზიისთვის ნისია გმართებთ, ჯერ ის უნდა დაიფაროს.' },
    { head: 'ცვლილებები.', body: 'მაღაზიას შეუძლია შეცვალოს ბონუსის პროცენტი, კლუბის ზღვარი და კლუბის ფასები. ცვლილება მოქმედებს შემდეგი ყიდვიდან და უკვე ნაყიდს არ ეხება.' },
  ],
}

/** A percent as typed („2", „2.5"); lari to the tetri when not whole („300", „12.50"). */
const pctText = (n: number) => String(Number(n.toFixed(2)))
const gelText = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2))

export function clubRulesText(shop: string, pct: number, threshold: number): ClubRules {
  const fill = (s: string) =>
    s.replace(/\{shop\}/g, shop).replace(/\{pct\}/g, pctText(pct)).replace(/\{threshold\}/g, gelText(threshold))
  return {
    title: fill(CLUB_RULES_TEMPLATE.title),
    items: CLUB_RULES_TEMPLATE.items.map(i => ({ head: fill(i.head), body: fill(i.body) })),
  }
}
